import { AuthConstantS } from '@/features/Auth/outbound/share';
import { APP_STORE_LINK, VALID_APP_VERSION } from '@/libs/constant/AppConstant';
import { AT, T } from '@/libs/translation';
import { compareAppVersion } from '@/libs/util/compareAppVersion';
import { Button, ButtonSnsIcon, Link, Typo } from '@/ui/component/atom';
import { Header, Layout } from '@/ui/component/cell';
import { LogoView, makePopup } from '@/ui/component/view';
import { getSharedData, PostMessage } from '@/ui/util';
import { filter, map, pipe, toArray, values } from '@fxts/core';
import { html, type Html, Page, View } from 'rune-ts';
import auth_klass from '../Common/Auth.module.scss';
import klass from './SignupPage.module.scss';

export interface SignupPageData {}

export class SignupPageBodyView extends View<SignupPageData> {
  override template() {
    const { client_id, theme, is_app, app_info } = getSharedData(this);
    return html`<div class="${auth_klass.page}">
      <div class="${auth_klass.container}">
        ${new LogoView({ theme })}
        <div class="${auth_klass.section}">
          <section class="${klass.sns_signin}">
            ${new Typo({ text: html.preventEscape(T('auth.signup.suggestion')) }, { typo: 'p1', weight: 'medium' })}
            <div class="${klass.button_container}">
              ${!(is_app && app_info?.os === 'IOS')
                ? new Link(
                    {
                      label: new ButtonSnsIcon(
                        { type: 'KAKAO', label: new Typo({ text: html.preventEscape(T('auth.login.start_kakao')) }) },
                        { active: true, button_type: 'fullsize_center' },
                      ),
                      href: `/oauth/request/kakao?client_id=${client_id}`,
                    },
                    { klass: `${klass.link} sns_login_link` },
                  )
                : ''}
              <div class="${klass.mini_button_container}">
                ${pipe(
                  AuthConstantS.USER_LOGIN_SNS_TYPE_MAPPER,
                  values,
                  filter((type) => {
                    if (is_app && app_info?.os === 'IOS') return true;
                    return type !== 'KAKAO';
                  }),
                  map((type) => {
                    return new Link(
                      {
                        label: new ButtonSnsIcon({ type }, { active: true }),
                        href: `/oauth/request/${type.toLowerCase()}?client_id=${client_id}`,
                      },
                      {
                        klass: 'sns_login_link',
                      },
                    );
                  }),
                  toArray,
                )}
              </div>
            </div>
          </section>
          <section class="${klass.signup_container}">
            ${new Typo({ text: T('common.or') }, { typo: 'p1', color: 'gray_80' })}
            ${new Link(
              {
                label: new Button(
                  { label: new Typo({ text: T('auth.signup.email') }, { typo: 'p1', weight: 'bold' }) },
                  { color: 'secondary', size: 'large' },
                ),
                href: `/${AT.language}/signup/email?client_id=${client_id}`,
              },
              { klass: klass.link },
            )}
            <div class="${klass.signup}">
              ${new Typo({ text: T('auth.signup.login_request') }, { typo: 'p1', color: 'gray_80', weight: 'medium' })}
              ${new Link({
                label: new Typo({ text: T('auth.signup.go_login') }, { typo: 'p1', weight: 'medium' }),
                href: `/${AT.language}/login?client_id=${client_id}`,
              })}
            </div>
          </section>
        </div>
      </div>
    </div>`;
  }

  override onRender() {
    const { app_info, theme } = getSharedData(this);
    this.delegate('click', '.sns_login_link', async (e) => {
      if (app_info && compareAppVersion(app_info.version, VALID_APP_VERSION[theme]) === -1) {
        e.originalEvent.preventDefault();
        const res = await makePopup({
          title: T('app.need_update.title'),
          message: T('app.need_update.message'),
          ok: T('app.need_update.ok'),
          cancel: T('common.cancel'),
        });

        if (res === 'confirm') {
          location.href = APP_STORE_LINK[theme][app_info.os];
        }
      }
    });
  }
}

export class SignupPage extends Page<SignupPageData> {
  protected override template(): Html {
    const { device } = getSharedData(this);
    return html`
      <div>
        ${new Layout(
          { view: new SignupPageBodyView(this.data) },
          {
            header: device === 'mo' ? new Header({}) : undefined,
          },
        )}
      </div>
    `;
  }

  override onRender() {
    this.delegate(Header.HeaderBackEvent, Header, () => {
      const { app_info } = getSharedData(this);

      if (app_info?.name === 'MarppleShop') {
        PostMessage.goBack();
      } else {
        history.back();
      }
    });
  }
}
